import { useMemo, useRef, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material';

import { FormNames, MidYearReportSchema, MidYearReportDocument } from '@youscience/commonapp-service-common';
import { brightpathTheme } from '@youscience/theme';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import useRecommenderStore from '@store/RecommenderStore';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { useUserProfile } from '@hooks/useUserProfile';

import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';

import { formatDateWithFallback } from '@utils/formatter';

import GPA, { GPAFields } from './components/GPA';
import ClassRank, { ClassRankFields } from './components/ClassRank';
import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';
import Summary, { SummaryFields } from './components/Summary';
import Transcripts, { TranscriptsFields } from './components/Transcripts';
import { FormState } from '@constants/commonApp/types';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import Submissions from '../../common/Submissions';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';
import {
  checkFormValidity,
  checkCounselorProfileValidity,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  PREVIEW_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import { useRemoveShowErrorOnUnmount, removeShowErrorsFromUrl } from '@utils/navigation';

const formName = FormNames.MIDYEAR_REPORT;

export const MidYearReport = () => {
  useRemoveShowErrorOnUnmount();

  const { notify } = useNotificationStore((state) => state);
  const { userId } = useUserProfile();
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const midYearReport = useApplicantForm<MidYearReportDocument>(formName);
  const initialFormState = useMemo(
    () => ({
      [TabSections.ClassRank]: midYearReport.content?.classRank,
      [TabSections.GPA]: midYearReport.content?.gpa,
      [TabSections.Transcripts]: midYearReport.content?.transcripts,
      [TabSections.Summary]: midYearReport.content?.summary,
    }),
    [midYearReport.content],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);
  const [expandedSection, setExpandedSection] = useState<TabSections | null>(TabSections.ClassRank);
  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const isSubmitted =
    midYearReport.metadata?.completionStatus === FormCompletionStatus.SUBMITTED ||
    midYearReport.metadata?.completionStatus === FormCompletionStatus.DOWNLOADED;
  const isSubmitDisabled = midYearReport.metadata?.availabilityStatus === FormAvailabilityStatus.NOT_AVAILABLE;
  const { applicantId, recommenderId } = midYearReport.metadata;

  useEffect(() => {
    if (isShowErrors && midYearReport.content) {
      const errors = validateForm();
      setFormErrors(errors);

      const sections = [TabSections.ClassRank, TabSections.GPA, TabSections.Transcripts, TabSections.Summary] as const;

      const fullValidation = MidYearReportSchema.safeParse(form.current);

      if (!fullValidation.success) {
        const firstErrorSection = sections.find((section) => {
          return fullValidation.error.issues.some((issue) => issue.path[0] === section);
        });

        if (firstErrorSection) {
          setExpandedSection(firstErrorSection);
          return;
        }
      }

      const firstErrorSection = sections.find((section) => {
        const sectionData = midYearReport.content?.[section];
        if (!sectionData) return false;

        try {
          const sectionFormState = { [section]: sectionData };
          checkFormValidity(sectionFormState, MidYearReportSchema);
          return false;
        } catch {
          return true;
        }
      });

      if (firstErrorSection) {
        setExpandedSection(firstErrorSection);
      }
    }
  }, [isShowErrors, midYearReport.content]);

  const toggleExpandedSelection = (section: TabSections) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const getNextSection = (section: TabSections): TabSections | undefined => {
    switch (section) {
      case TabSections.ClassRank:
        return TabSections.GPA;
      case TabSections.GPA:
        return TabSections.Transcripts;
      case TabSections.Transcripts:
        return TabSections.Summary;
      case TabSections.Summary:
        return undefined;
    }
  };

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const formValid = checkFormValidity(form.current, MidYearReportSchema);
    const profileValid = checkCounselorProfileValidity(recommenderProfile);

    return {
      form: !formValid,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (options?: {
    isHideNotification?: boolean;
    section?: keyof typeof initialFormState;
  }) => {
    const { isHideNotification, section } = options || {};
    const data = section ? { [section]: form.current[section] } : form.current;
    await midYearReport.save({ ...data, applicantId, recommenderId });

    if (!isHideNotification) {
      midYearReport.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
    }

    if (section && !midYearReport.isError) {
      toggleExpandedSelection(section);
      const nextSection = getNextSection(section);
      if (nextSection) toggleExpandedSelection(nextSection);
    }
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await midYearReport.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater({ isHideNotification: true });

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        removeShowErrorsFromUrl();
        await midYearReport.preview({ ...form.current, applicantId, recommenderId });
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string) => {
    await midYearReport.submit({
      applicantId,
      recommenderId,
      counselorApproval: {
        isReviewed: true,
        isAffirmed: true,
        signature,
        signedDate,
        userId: userId as string,
      },
    });

    midYearReport.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnUnsubmit = async () => {
    await midYearReport.unsubmit({ applicantId, recommenderId });

    midYearReport.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  if (!midYearReport.isReady) {
    return null;
  }

  return (
    <ThemeProvider theme={brightpathTheme}>
      <FormHeader
        name="Mid Year Report"
        tooltipTitle="The mid year report shares information about your student's academic history. It's also where you provide their most up-to-date transcripts."
        deadline={formatDateWithFallback(midYearReport.metadata?.deadline)}
        status={midYearReport.metadata?.completionStatus || FormCompletionStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={midYearReport.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />
      {isSubmitted ? (
        <Submissions formName={formName} onSubmit={handleOnSubmit} />
      ) : (
        <>
          <ClassRank
            classRank={midYearReport.content?.classRank || recommenderProfile?.schoolProfile?.classRank}
            isExtended={expandedSection === TabSections.ClassRank}
            onExpand={() => toggleExpandedSelection(TabSections.ClassRank)}
            onFormChange={onFormChangeHandler<ClassRankFields>(TabSections.ClassRank)}
            onContinue={() => handleSaveForLater({ section: TabSections.ClassRank })}
            isSaved={midYearReport.sectionIsSaved.classRank}
            showError={
              isShowErrors &&
              !MidYearReportSchema.shape.classRank.safeParse(form.current[TabSections.ClassRank]).success
            }
          />

          <GPA
            gpa={midYearReport.content?.gpa || recommenderProfile?.schoolProfile?.gpa}
            isExtended={expandedSection === TabSections.GPA}
            onExpand={() => toggleExpandedSelection(TabSections.GPA)}
            onFormChange={onFormChangeHandler<GPAFields>(TabSections.GPA)}
            onContinue={() => {
              handleSaveForLater({ section: TabSections.GPA });
            }}
            isSaved={midYearReport.sectionIsSaved.gpa}
            showError={isShowErrors && !MidYearReportSchema.shape.gpa.safeParse(form.current[TabSections.GPA]).success}
          />

          <Transcripts
            transcripts={
              midYearReport.content?.transcripts ||
              (recommenderProfile?.schoolProfile?.transcripts as TranscriptsFields)
            }
            isExtended={expandedSection === TabSections.Transcripts}
            onExpand={() => toggleExpandedSelection(TabSections.Transcripts)}
            onFormChange={onFormChangeHandler<TranscriptsFields>(TabSections.Transcripts)}
            onContinue={() => handleSaveForLater({ section: TabSections.Transcripts })}
            isSaved={midYearReport.sectionIsSaved.transcripts}
            applicantName={midYearReport.metadata?.studentName}
            showError={
              isShowErrors &&
              !MidYearReportSchema.shape.transcripts.safeParse(form.current[TabSections.Transcripts]).success
            }
          />

          <Summary
            summary={midYearReport.content?.summary}
            isExtended={expandedSection === TabSections.Summary}
            onExpand={() => toggleExpandedSelection(TabSections.Summary)}
            onFormChange={onFormChangeHandler<SummaryFields>(TabSections.Summary)}
            isSaved={midYearReport.sectionIsSaved.summary}
            applicantName={midYearReport.metadata.studentName}
            showError={
              isShowErrors && !MidYearReportSchema.shape.summary.safeParse(form.current[TabSections.Summary]).success
            }
          />
        </>
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={midYearReport.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title="Mid Year Report"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={midYearReport.isLoadingPreview}
          reviewType={reviewType}
          pdf={midYearReport.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </ThemeProvider>
  );
};
